import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { orderCells } from "../helpers/column_order"
import { handoff } from "../helpers/position_grid_handoff"
import { componentId, isComponentRow, positionRowId } from "../helpers/position_grid_rows"

// The structure half of the position grid (e.g. billing/position_grid/_grid.html.erb): new rows,
// indent and outdent, moving a row among its siblings, deleting. position-tree beside it owns
// folding and search; both claim their keys through `grid-cells:command`.
//
// Every command goes through the page's items controller (`itemsUrl`, e.g.
// Billing::LineItemsController). `paramScope` is that controller's form scope
// (`billing_line_item`), the only thing the requests do not share. Its answer either syncs the
// rows that changed (`position_grid_sync`, PositionGrid::GridSync — then `position-grid:synced`
// arrives here) or, where too much changed, re-renders the whole grid (then this controller
// reconnects). Either way the selection is handed across in `handoff`, keyed by the grid's storage
// key: the row to select, or — for a row that did not exist before — the ids that did, so the new
// one is the row not among them.
//
// A new row is typed into a draft first. A Kurztext is required, so nothing is created until one
// is there: Enter or Tab posts it, Esc (or leaving it empty) drops the draft without a trace. The
// draft opens from the keys, the toolbar or the ＋ a pointer finds on a row (placeInsertHandle).
//
// While position-tree has the list searched, filtered or sorted, new rows, copies and deletes go on
// — each names its place in the tree — but moves and indents wait for the OZ order (claimInOrder).
// A Kalkulation row keeps its order under the sort, so only a search or a filter stops its moves.
//
// Undo, redo and duplicate are answered by the server with the row to select (`select`), which
// the client cannot know beforehand — undo may well bring back a row that is not on screen.
//
// The Kalkulation rows of the costing view (`data-kind="component"`) are rows of their position, not
// line items. Where the account may change them (`componentsUrl`, Billing::LineItems::
// CalculationComponentsController), Strg+Alt+Enter drafts a new one — under the selected one, or
// last under the selected position —, and on one of them Strg+Shift+D copies, Strg+Entf deletes and
// Alt+Shift+↑/↓ moves it among its position's rows. Otherwise those keys say what the row is.
const ARROWS = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]
// What a dragged row carries (dragStart), so a drag of anything else is never taken for one.
const DRAG_TYPE = "application/x-position-grid-row"

export default class extends Controller {
  static targets = ["draft", "notice", "insertHandle", "insertMenu"]
  static values = {
    storageKey: String,
    editable: Boolean,
    itemsUrl: String,
    paramScope: String,
    undoUrl: String,
    redoUrl: String,
    select: String,
    messages: Object,
    componentsUrl: String,
  }

  connect() {
    this.pendingDelete = null
    this.draft = null
    this.onPointerMove = (event) => this.placeInsertHandle(event)
    this.onPointerLeave = () => {
      if (!this.insertMenuOpen) this.hideInsertHandle()
    }
    this.onScroll = () => this.hideInsertHandle()
    this.onPointerDown = (event) => this.closeInsertMenuOutside(event)
    this.element.addEventListener("pointermove", this.onPointerMove)
    this.element.addEventListener("pointerleave", this.onPointerLeave)
    document.addEventListener("scroll", this.onScroll, true)
    document.addEventListener("pointerdown", this.onPointerDown, true)
    this.takeHandoff()
  }

  disconnect() {
    this.element.removeEventListener("pointermove", this.onPointerMove)
    this.element.removeEventListener("pointerleave", this.onPointerLeave)
    document.removeEventListener("scroll", this.onScroll, true)
    document.removeEventListener("pointerdown", this.onPointerDown, true)
  }

  // The rows a step changed have been synced in place (position_grid_sync). Nothing reconnected,
  // so the draft the step was typed into is forgotten here, and the row the server names (an undo,
  // a duplicate) arrives with the event rather than as a re-rendered value — none named clears it,
  // as a re-render did: an earlier step's row is not this one's.
  synced(event) {
    this.draft = null
    this.selectValue = event.detail?.select || ""
    this.takeHandoff()
  }

  takeHandoff() {
    const pending = handoff.get(this.storageKeyValue)
    if (!pending) return

    handoff.delete(this.storageKeyValue)
    // After the other controllers on the new grid have connected — grid-cells must be listening
    // for the click that selects.
    setTimeout(() => this.restore(pending), 0)
  }

  get rows() {
    return Array.from(this.element.querySelectorAll("tr[data-grid-row]"))
  }

  row(id) {
    return id ? this.element.querySelector(`tr[data-grid-row][data-row-id="${id}"]`) : null
  }

  // -- keys -------------------------------------------------------------------

  command(event) {
    if (!this.editableValue) return

    const { originalEvent: key, rowId, column } = event.detail
    const mod = key.ctrlKey || key.metaKey
    if (this.pendingDelete?.rowId === rowId) return this.answerDelete(event, key)
    // The selection moved on by mouse since the question: it is not answered from another row.
    if (this.pendingDelete) this.dismissDelete()

    const letter = key.key.toLowerCase()
    // A Kalkulation row (the costing view) is no line item: a new position goes under its position,
    // and the commands that would move, copy or delete it act on the Kalkulation row — or, where
    // the account may not change those, say so instead.
    const component = isComponentRow(this.row(rowId))
    // Without the Kalkulation rows' routes, Strg+Alt+Enter stays what Strg+Enter is.
    if (mod && key.altKey && !key.shiftKey && key.key === "Enter" && this.componentsUrlValue)
      return this.claim(event, () => this.addComponent(rowId, column))
    if (
      component &&
      this.componentsUrlValue &&
      this.componentCommand(event, key, mod, letter, rowId, column)
    )
      return
    if (component && this.componentRefused(key, mod, letter))
      return this.claim(event, () => this.notify(this.messagesValue.component_row))

    if (mod && !key.altKey && letter === "z") {
      event.preventDefault()
      this.replay(key.shiftKey ? "redo" : "undo", column)
    } else if (key.ctrlKey && !key.metaKey && !key.altKey && letter === "y") {
      // Strg+Y is redo on Windows, as in Excel.
      event.preventDefault()
      this.replay("redo", column)
    } else if (mod && key.shiftKey && !key.altKey && letter === "d") {
      this.claim(event, () => this.duplicate(rowId, column))
    } else if (mod && key.altKey && !key.shiftKey && key.code === "KeyT") {
      // Strg/Cmd+Alt+T: a Textposition. By `code`, since Alt turns the T into "†" on a Mac.
      this.claim(event, () => this.insertDraft("text", this.positionRowId(rowId), column))
    } else if (mod && key.key === "Enter") {
      this.claim(event, () =>
        this.insertDraft(key.shiftKey ? "group" : "position", this.positionRowId(rowId), column),
      )
    } else if (key.altKey && key.shiftKey && !mod && ARROWS.includes(key.key)) {
      this.claimInOrder(event, () => this.arrowCommand(key.key, rowId, column))
    } else if (mod && (key.key === "Backspace" || key.key === "Delete")) {
      this.claim(event, () => this.askDelete(rowId, column))
    }
  }

  componentRefused(key, mod, letter) {
    return (
      (mod && key.shiftKey && !key.altKey && letter === "d") ||
      (key.altKey && key.shiftKey && !mod && ARROWS.includes(key.key)) ||
      (mod && (key.key === "Backspace" || key.key === "Delete"))
    )
  }

  // Strg+Shift+D, Strg+Entf and Alt+Shift+arrows on a Kalkulation row. True when the key was one.
  componentCommand(event, key, mod, letter, rowId, column) {
    if (mod && key.shiftKey && !key.altKey && letter === "d") {
      this.claim(event, () => this.duplicateComponent(rowId, column))
    } else if (key.altKey && key.shiftKey && !mod && ARROWS.includes(key.key)) {
      this.claim(event, () => this.componentArrow(key.key, rowId, column))
    } else if (mod && (key.key === "Backspace" || key.key === "Delete")) {
      this.claim(event, () => this.deleteComponent(rowId, column))
    } else {
      return false
    }
    return true
  }

  // The position a Kalkulation row belongs to; any other row is itself.
  positionRowId(rowId) {
    return positionRowId(this.row(rowId), rowId)
  }

  // Claims the key and runs the command. A new row, a copy or a delete names its place in the tree
  // (a parent and the sibling it follows), not on screen, so it runs whatever the list shows.
  claim(event, run) {
    event.preventDefault()
    run()
  }

  // A move or an indent goes by the row's neighbours on screen, which are not its neighbours in the
  // tree while the list is searched, filtered or sorted — it waits for the OZ order.
  claimInOrder(event, run) {
    event.preventDefault()
    if (this.narrowed) return this.notify(this.messagesValue.order_needed)

    run()
  }

  // position-tree marks the grid while it is searched, filtered or sorted.
  get narrowed() {
    return this.element.hasAttribute("data-position-tree-narrowed") || this.searchTyped
  }

  // Searched or filtered, not merely sorted: rows may be left out.
  get filtered() {
    return this.element.hasAttribute("data-position-tree-filtered") || this.searchTyped
  }

  // Typed, perhaps not yet applied.
  get searchTyped() {
    return !!this.element.querySelector('[data-position-tree-target="search"]')?.value.trim()
  }

  arrowCommand(key, rowId, column) {
    if (key === "ArrowRight") this.indent(rowId, column)
    else if (key === "ArrowLeft") this.outdent(rowId, column)
    else this.move(rowId, column, key === "ArrowUp" ? -1 : 1)
  }

  // Toolbar buttons: under the selected row, or at the end of the document when nothing is
  // selected (an empty document has no row to press Strg+Enter on).
  addPosition() {
    this.toolbarInsert("position")
  }

  addGroup() {
    this.toolbarInsert("group")
  }

  addText() {
    this.toolbarInsert("text")
  }

  // The same rules as the keys.
  toolbarInsert(kind) {
    if (this.draftInTheWay()) return

    this.insertDraft(kind, this.positionRowId(this.selectedRowId), null)
  }

  // The toolbar's "+ Bestandteil" (the Kalkulation view): the same as Strg+Alt+Enter on the selected
  // row — under a Kalkulation row, or after a position's last. Without a selected position it says so.
  addComponentFromToolbar() {
    if (this.draftInTheWay()) return

    const column = this.element.querySelector("td[data-selected]")?.dataset.column ?? null
    this.addComponent(this.selectedRowId, column)
  }

  // A draft already typed into is finished first: replacing it would throw its text away, and once
  // it is sent, the create's re-render would wipe the new draft. True when one is in the way.
  draftInTheWay() {
    if (this.draft?.sending) return true
    if (!this.draft?.input.value.trim()) return false

    this.draft.input.focus()
    return true
  }

  get selectedRowId() {
    return this.element.querySelector("td[data-selected]")?.closest("tr")?.dataset.rowId ?? null
  }

  // -- the ＋ handle ----------------------------------------------------------------

  // The ＋ on the row edge nearest the pointer (position_grid/_insert_handle), in the checkbox column:
  // over a row's lower half the edge under it, over its upper half the edge above — the lower edge
  // of the row on screen before it. So an edge means the same row from either side, and the pointer
  // can cross it onto the handle. The menu opens the drafts the keys open on the selected row, on
  // that row. Not while a draft is open or a row is dragged, and not for a finger — there is no
  // hover to find it. Over the handle itself it stays where it is.
  placeInsertHandle(event) {
    if (!this.hasInsertHandleTarget || !this.editableValue || this.insertMenuOpen) return
    if (event.pointerType === "touch" || this.insertHandleTarget.contains(event.target)) return

    const hovered = event.target.closest?.("tr[data-grid-row]")
    if (!hovered || this.draft || this.dragging) return this.hideInsertHandle()

    const box = hovered.getBoundingClientRect()
    const upper = event.clientY < box.top + box.height / 2
    const row = (upper && this.previousShownRow(hovered)) || hovered
    if (row.dataset.rowId === this.insertRowId && !this.insertHandleTarget.hidden) return

    const gutter = row.querySelector("td[data-leading]") ?? row.querySelector("td[data-column]")
    if (!gutter) return this.hideInsertHandle()

    const cell = gutter.getBoundingClientRect()
    const handle = this.insertHandleTarget
    handle.style.left = `${gutter.matches("[data-leading]") ? cell.left + cell.width / 2 : cell.left + 8}px`
    handle.style.top = `${row.getBoundingClientRect().bottom}px`
    handle.hidden = false
    this.insertRowId = row.dataset.rowId
  }

  previousShownRow(row) {
    let previous = row.previousElementSibling
    while (previous && (previous.hidden || !previous.matches("tr[data-grid-row]")))
      previous = previous.previousElementSibling
    return previous
  }

  // Focus inside the handle or its menu would fall to the page as they hide; it goes back to the
  // grid's selected cell, so the keys go on working.
  hideInsertHandle() {
    if (!this.hasInsertHandleTarget) return

    const focused =
      this.insertHandleTarget.contains(document.activeElement) ||
      this.insertMenuTarget.contains(document.activeElement)
    this.closeInsertMenu()
    this.insertHandleTarget.hidden = true
    this.insertRowId = null
    if (focused) this.element.querySelector('td[data-column][tabindex="0"]')?.focus()
  }

  get insertMenuOpen() {
    return this.hasInsertMenuTarget && !this.insertMenuTarget.hidden
  }

  toggleInsertMenu() {
    if (this.insertMenuOpen) return this.closeInsertMenu()

    const row = this.row(this.insertRowId)
    if (!row) return this.hideInsertHandle()

    const menu = this.insertMenuTarget
    // A Bestandteil only under a position or one of its Kalkulation rows, as Strg+Alt+Enter.
    const component = menu.querySelector('[data-insert-kind="component"]')
    if (component) {
      component.hidden =
        row.dataset.kind === "group" ||
        row.dataset.kind === "text" ||
        (!isComponentRow(row) && row.dataset.positionType === "discount")
    }
    const handle = this.insertHandleTarget.getBoundingClientRect()
    menu.hidden = false
    menu.style.left = `${handle.right + 4}px`
    menu.style.top = `${Math.min(handle.top, window.innerHeight - menu.offsetHeight - 8)}px`
    this.insertHandleTarget.querySelector("button")?.setAttribute("aria-expanded", "true")
    menu.querySelector("button:not([hidden])")?.focus()
  }

  closeInsertMenu() {
    if (!this.insertMenuOpen) return

    this.insertMenuTarget.hidden = true
    this.insertHandleTarget.querySelector("button")?.setAttribute("aria-expanded", "false")
  }

  // Esc in the menu: the handle stays, and has the focus the menu had.
  escapeInsertMenu() {
    this.closeInsertMenu()
    this.insertHandleTarget.querySelector("button")?.focus()
  }

  closeInsertMenuOutside(event) {
    if (!this.insertMenuOpen) return
    if (this.insertMenuTarget.contains(event.target)) return
    if (this.insertHandleTarget.contains(event.target)) return

    this.hideInsertHandle()
  }

  // The toolbar's rules, on the row the handle was on rather than the selected one.
  insertFromHandle(event) {
    const kind = event.params.kind
    const rowId = this.insertRowId
    this.hideInsertHandle()
    if (this.draftInTheWay() || !this.row(rowId)) return

    if (kind === "component") this.addComponent(rowId, null)
    else this.insertDraft(kind, this.positionRowId(rowId), null)
  }

  // -- tree helpers -----------------------------------------------------------

  siblingsOf(row) {
    const parent = row.dataset.parentId ?? ""
    return this.rows.filter((candidate) => (candidate.dataset.parentId ?? "") === parent)
  }

  // The last row of `row`'s subtree, in document order — the row itself when it has none.
  subtreeEnd(row) {
    const rows = this.rows
    const depth = Number(row.dataset.depth)
    let end = rows.indexOf(row)
    while (rows[end + 1] && Number(rows[end + 1].dataset.depth) > depth) end += 1
    return rows[end]
  }

  // -- new rows ---------------------------------------------------------------

  // Where a new row lands, as Excel users expect it: a position goes under the selected one, or
  // to the top of a selected Titel; a Titel goes after the selected Titel's whole subtree, or after
  // the Titel the selected position sits in. `insertAfter` is what #create understands: a
  // sibling's id, "start", or "" for the end.
  placement(kind, anchor) {
    if (kind === "component") return this.componentPlacement(anchor)
    if (!anchor) return { parentId: "", insertAfter: "", after: this.rows.at(-1), depth: 0 }

    const depth = Number(anchor.dataset.depth)
    const group = anchor.dataset.kind === "group"
    // A Textposition goes where a position would: it explains what follows it, inside the Titel.
    const rowLike = kind === "position" || kind === "text"
    if (rowLike && group) {
      return {
        parentId: anchor.dataset.rowId,
        insertAfter: "start",
        after: anchor,
        depth: depth + 1,
        expand: anchor.dataset.rowId,
      }
    }
    // After the position's own rows — its Kalkulation, in the costing view — not between them.
    if (rowLike || (!group && !anchor.dataset.parentId)) {
      return {
        parentId: anchor.dataset.parentId ?? "",
        insertAfter: anchor.dataset.rowId,
        after: this.subtreeEnd(anchor),
        depth,
      }
    }

    const titel = group ? anchor : this.row(anchor.dataset.parentId)
    return {
      parentId: titel.dataset.parentId ?? "",
      insertAfter: titel.dataset.rowId,
      after: this.subtreeEnd(titel),
      depth: Number(titel.dataset.depth),
    }
  }

  // A new Kalkulation row: after the selected one, or after the selected position's last.
  componentPlacement(anchor) {
    const component = isComponentRow(anchor)
    const position = component ? this.row(anchor.dataset.parentId) : anchor
    return {
      positionId: position.dataset.rowId,
      insertAfter: component ? componentId(anchor) : "",
      after: component ? anchor : this.subtreeEnd(position),
      depth: Number(position.dataset.depth) + 1,
      expand: position.dataset.rowId,
    }
  }

  insertDraft(kind, rowId, column) {
    if (!this.hasDraftTarget) return

    this.dropDraft()
    const anchor = this.row(rowId)
    const place = this.placement(kind, anchor)
    if (place.expand) this.dispatch("expand", { detail: { ids: [place.expand] } })

    const row = this.draftTarget.content.firstElementChild.cloneNode(true)
    this.followColumnOrder(row)
    row.querySelector("[data-position-draft-indent]").style.paddingLeft = `${place.depth * 1.25}rem`
    const input = row.querySelector("[data-position-draft-input]")
    input.placeholder = this.messagesValue[`draft_${kind}`]
    input.setAttribute("aria-label", input.placeholder)

    if (place.after) place.after.after(row)
    else this.element.querySelector("tbody").prepend(row)

    this.draft = { kind, row, input, place, returnTo: anchor && { rowId, column } }
    this.listenToDraft(input)
    input.focus()
  }

  // The template was rendered in the column order the page loaded with; a header dragged since
  // (grid-columns) moved the live rows only. The draft follows the header, by grid-columns' rule.
  followColumnOrder(row) {
    const keys = [...this.element.querySelectorAll("thead th[data-column]")].map(
      (th) => th.dataset.column,
    )
    orderCells(row, keys)
  }

  // The draft is not a grid row, so grid-cells must not see its keys, paste or copy at all: a
  // paste into the draft would otherwise also be written into whichever cell is selected.
  listenToDraft(input) {
    input.addEventListener("keydown", (event) => this.draftKeydown(event))
    input.addEventListener("paste", (event) => event.stopPropagation())
    input.addEventListener("copy", (event) => event.stopPropagation())
    input.addEventListener("blur", () => setTimeout(() => this.draftLeft(input), 0))
  }

  draftKeydown(event) {
    event.stopPropagation()
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancelDraft()
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      this.commitDraft()
    }
  }

  // Leaving the draft commits what was typed, as leaving a cell does; leaving it empty drops it.
  draftLeft(input) {
    if (this.draft?.input !== input || this.draft.sending) return
    if (document.activeElement === input) return

    this.commitDraft({ refocus: false })
  }

  commitDraft({ refocus = true } = {}) {
    // A repeated Enter must not post the same row twice before the re-render removes the draft.
    if (!this.draft || this.draft.sending) return

    const text = this.draft.input.value.trim()
    if (!text) return refocus ? this.cancelDraft() : this.dropDraft()

    const { kind, place } = this.draft
    if (kind === "component") return this.commitComponentDraft(text, place)

    const body = new FormData()
    const scope = this.paramScopeValue
    body.append(`${scope}[kind]`, kind)
    body.append(`${scope}[short_text]`, text)
    if (place.parentId) body.append(`${scope}[parent_id]`, place.parentId)
    if (place.insertAfter) body.append("insert_after", place.insertAfter)

    // A new Titel is named and stays selected; a new position goes on to its Menge; a new
    // Textposition opens its text, where the typed line already stands as the first paragraph.
    const column = kind === "position" ? "quantity" : "short_text"
    this.draft.sending = true
    this.send("POST", this.itemsUrlValue, body, {
      knownIds: new Set(this.rows.map((row) => row.dataset.rowId)),
      column,
      openText: kind === "text",
    })
  }

  // A new Kalkulation row goes on to its Ansatz.
  commitComponentDraft(text, place) {
    const body = new FormData()
    body.append("calculation_component[description]", text)
    if (place.insertAfter) body.append("insert_after", place.insertAfter)
    this.draft.sending = true
    this.send("POST", this.componentsUrl(place.positionId), body, {
      knownIds: new Set(this.rows.map((row) => row.dataset.rowId)),
      column: "costing_quantity",
    })
  }

  cancelDraft() {
    const returnTo = this.draft?.returnTo
    this.dropDraft()
    if (returnTo) this.select(returnTo)
  }

  dropDraft() {
    this.draft?.row.remove()
    this.draft = null
  }

  // -- moving -----------------------------------------------------------------

  // Under the Titel directly above, as Billing::LineItemsController#indent does. Asked here
  // first, so a refused indent says why instead of re-rendering an unchanged table.
  indent(rowId, column) {
    const row = this.row(rowId)
    const siblings = this.siblingsOf(row)
    const previous = siblings[siblings.indexOf(row) - 1]
    if (previous?.dataset.kind !== "group")
      return this.notify(this.messagesValue.indent_needs_group)

    this.dispatch("expand", { detail: { ids: [previous.dataset.rowId] } })
    this.send("PATCH", `${this.itemsUrlValue}/${rowId}/indent`, null, { rowId, column })
  }

  outdent(rowId, column) {
    if (!this.row(rowId).dataset.parentId) return this.notify(this.messagesValue.outdent_at_top)

    this.send("PATCH", `${this.itemsUrlValue}/${rowId}/outdent`, null, { rowId, column })
  }

  // One place up or down among its siblings, its subtree travelling with it — written as the full
  // sibling order, which is what #reorder takes from a drag.
  move(rowId, column, step) {
    const row = this.row(rowId)
    const siblings = this.siblingsOf(row)
    const index = siblings.indexOf(row)
    const target = index + step
    if (target < 0) return this.notify(this.messagesValue.move_first)
    if (target >= siblings.length) return this.notify(this.messagesValue.move_last)

    const order = siblings.map((sibling) => sibling.dataset.rowId)
    ;[order[index], order[target]] = [order[target], order[index]]
    this.reorder(row.dataset.parentId ?? "", order, rowId, column)
  }

  // #reorder's request: the full sibling order under `parentId`, and which row the step was about,
  // for the undo log — moved or dragged among its siblings, it goes nowhere else.
  reorder(parentId, order, rowId, column) {
    const body = new FormData()
    body.append("parent_id", parentId)
    order.forEach((id, position) => {
      body.append("positions[][id]", id)
      body.append("positions[][position]", position + 1)
    })
    body.append("moved_id", rowId)
    this.send("PATCH", `${this.itemsUrlValue}/reorder`, body, { rowId, column })
  }

  // -- dragging rows ----------------------------------------------------------------

  // A row is dragged by the grip in its checkbox cell (PositionGrid::RowComponent#leading_cell),
  // with its subtree, and dropped before or after another row — or into a Titel, over its middle,
  // as its last row. The drop is #reorder's request, the one `move` sends for Alt+Shift+↑/↓, so it
  // is logged for Strg+Z. Not while the list is searched, filtered or sorted: the rows on screen
  // are not the real siblings then. The listeners sit on the grid (dragstart bubbles from the grip).
  //
  // The dragged rows are held by id: a cell save committed as the grip is grabbed streams its row
  // back mid-drag, and the element taken at the start is then no longer in the grid. The drag
  // carries its own type, so a stale one (its dragend fired on a removed grip and never arrived)
  // is never taken for a drag of something else.
  dragStart(event) {
    const grip = event.target.closest?.("[data-row-grip]")
    if (!grip) return

    const row = grip.closest("tr[data-grid-row]")
    this.hideInsertHandle()
    if (!this.editableValue || !row || this.narrowed) {
      event.preventDefault()
      if (row && this.narrowed) this.notify(this.messagesValue.order_needed)
      return
    }

    const rows = this.rows
    const subtree = rows.slice(rows.indexOf(row), rows.indexOf(this.subtreeEnd(row)) + 1)
    this.dragging = {
      id: row.dataset.rowId,
      ids: new Set(subtree.map((dragged) => dragged.dataset.rowId)),
    }
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData(DRAG_TYPE, row.dataset.rowId)
    event.dataTransfer.setDragImage(row, 16, 12)
    // After the browser has taken its picture of the row.
    requestAnimationFrame(() =>
      this.draggedRows().forEach((dragged) => dragged.setAttribute("data-dragging", "")),
    )
  }

  dragOver(event) {
    if (!this.dragging || !event.dataTransfer?.types?.includes(DRAG_TYPE)) return

    const target = event.target.closest?.("tr[data-grid-row]")
    const drop = target ? this.dropAt(target, event.clientY) : null
    this.markDrop(drop)
    if (!drop) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  // Over a position: before or after it, by its halves. Over a Titel: its top quarter is before it,
  // its bottom quarter after it — its whole subtree, marked under its last row — and the rest into
  // it. That bottom quarter is the only way behind an unfolded Titel that ends the list, or its
  // parent: the row after it is its own last row. Never onto the dragged rows themselves.
  //
  // A Kalkulation row is no place of its own: over it, the drop goes after its position. A position
  // marks "after" under its last Kalkulation row, where the dropped row will land.
  dropAt(target, y) {
    if (isComponentRow(target)) {
      const position = this.row(target.dataset.parentId)
      if (!position || this.dragging.ids.has(position.dataset.rowId)) return null
      return { target: position, zone: "after", mark: this.subtreeEnd(position) }
    }
    if (this.dragging.ids.has(target.dataset.rowId)) return null

    const box = target.getBoundingClientRect()
    const ratio = (y - box.top) / box.height
    if (target.dataset.kind !== "group") {
      const zone = ratio < 0.5 ? "before" : "after"
      return { target, zone, mark: zone === "after" ? this.subtreeEnd(target) : target }
    }
    if (ratio < 0.25) return { target, zone: "before", mark: target }
    if (ratio > 0.75) return { target, zone: "after", mark: this.subtreeEnd(target) }
    return { target, zone: "into", mark: target }
  }

  markDrop(drop) {
    const mark = this.dropMark
    if (mark && (mark.mark !== drop?.mark || mark.zone !== drop?.zone))
      mark.mark.removeAttribute("data-drop")
    this.dropMark = drop
    drop?.mark.setAttribute("data-drop", drop.zone)
  }

  drop(event) {
    const drop = this.dropMark
    const id = this.dragging?.id
    this.dragEnd()
    // Both looked up again: either may have been streamed back since.
    const row = this.row(id)
    const target = this.row(drop?.target.dataset.rowId)
    if (!row || !target) return

    event.preventDefault()
    const into = drop.zone === "into"
    const parentId = into ? target.dataset.rowId : (target.dataset.parentId ?? "")
    const siblings = this.rows.filter(
      (candidate) => (candidate.dataset.parentId ?? "") === parentId && candidate !== row,
    )
    let index = into ? siblings.length : siblings.indexOf(target)
    if (drop.zone === "after") index += 1
    siblings.splice(index, 0, row)

    const order = siblings.map((sibling) => sibling.dataset.rowId)
    const unchanged =
      (row.dataset.parentId ?? "") === parentId &&
      order.join() ===
        this.siblingsOf(row)
          .map((sibling) => sibling.dataset.rowId)
          .join()
    if (unchanged) return

    if (into) this.dispatch("expand", { detail: { ids: [parentId] } })
    this.reorder(parentId, order, id, "short_text")
  }

  dragEnd() {
    this.markDrop(null)
    this.draggedRows().forEach((dragged) => dragged.removeAttribute("data-dragging"))
    this.dragging = null
  }

  draggedRows() {
    const ids = this.dragging?.ids
    return ids ? this.rows.filter((row) => ids.has(row.dataset.rowId)) : []
  }

  // -- Kalkulation rows -------------------------------------------------------------

  componentsUrl(positionId, suffix = "") {
    return `${this.componentsUrlValue.replace("__position__", encodeURIComponent(positionId))}${suffix}`
  }

  // On a position or one of its Kalkulation rows; a Titel and a Nachlass have no calculation.
  addComponent(rowId, column) {
    const anchor = this.row(rowId)
    if (!anchor || anchor.dataset.kind === "group" || anchor.dataset.kind === "text")
      return this.notify(this.messagesValue.component_needs_position)
    if (anchor.dataset.positionType === "discount")
      return this.notify(this.messagesValue.component_discount)

    this.insertDraft("component", rowId, column)
  }

  duplicateComponent(rowId, column) {
    const row = this.row(rowId)
    this.send(
      "POST",
      this.componentsUrl(row.dataset.parentId, `/${componentId(row)}/duplicate`),
      null,
      {
        column,
        fromServer: true,
      },
    )
  }

  // Up or down among its position's Kalkulation rows; they never leave their position.
  // A search or a filter can leave its neighbours out — then it waits, as a position's move does.
  componentArrow(key, rowId, column) {
    if (key === "ArrowLeft" || key === "ArrowRight")
      return this.notify(this.messagesValue.component_indent)
    if (this.filtered) return this.notify(this.messagesValue.order_needed)

    const row = this.row(rowId)
    const siblings = this.componentsOf(row.dataset.parentId)
    const index = siblings.indexOf(row)
    const target = index + (key === "ArrowUp" ? -1 : 1)
    if (target < 0) return this.notify(this.messagesValue.component_move_first)
    if (target >= siblings.length) return this.notify(this.messagesValue.component_move_last)

    const ids = siblings.map(componentId)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    const body = new FormData()
    ids.forEach((id) => body.append("ids[]", id))
    body.append("moved_id", componentId(row))
    this.send("PATCH", this.componentsUrl(row.dataset.parentId, "/reorder"), body, {
      rowId,
      column,
    })
  }

  // At once, as a position: Strg+Z brings it back. The selection goes to the next Kalkulation row,
  // else the one above, else the position.
  deleteComponent(rowId, column) {
    const row = this.row(rowId)
    const siblings = this.componentsOf(row.dataset.parentId)
    const index = siblings.indexOf(row)
    const next = siblings[index + 1] ?? siblings[index - 1] ?? this.row(row.dataset.parentId)
    this.send("DELETE", this.componentsUrl(row.dataset.parentId, `/${componentId(row)}`), null, {
      rowId: next?.dataset.rowId,
      column,
    })
  }

  componentsOf(positionId) {
    return this.rows.filter((row) => isComponentRow(row) && row.dataset.parentId === positionId)
  }

  // -- undo, redo, duplicate ----------------------------------------------------

  // Allowed during a search: the answer re-renders the whole grid, the search included.
  // Waits for a cell save still on its way (Enter, then Strg+Z at once): sent alongside it, the
  // undo could reach the server first and take back the step before the one just typed.
  replay(direction, column) {
    const url = direction === "undo" ? this.undoUrlValue : this.redoUrlValue
    const send = () => this.send("POST", url, null, { column, fromServer: true })
    if (this.saving()) this.savesSettled().then(send)
    else send()
  }

  // grid-cells marks a cell `data-saving` until its answer is in; a successful answer replaces
  // the row, so the mark goes either way.
  saving() {
    return this.element.querySelector("[data-saving]") !== null
  }

  savesSettled() {
    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (this.saving()) return
        observer.disconnect()
        resolve()
      })
      observer.observe(this.element, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["data-saving"],
      })
    })
  }

  duplicate(rowId, column) {
    this.send("POST", `${this.itemsUrlValue}/${rowId}/duplicate`, null, {
      column,
      fromServer: true,
    })
  }

  // -- deleting ---------------------------------------------------------------

  // A single row goes at once — Strg+Z brings it back. A Titel with rows under it is asked about
  // first, in the toolbar rather than a browser dialog: Enter deletes, anything else keeps it. So
  // is a row the server reports it could not bring back (409, see deleteRow).
  askDelete(rowId, column) {
    const row = this.row(rowId)
    // The rows that go with it — a position's Kalkulation rows are its own, not rows under it.
    const count = this.rows
      .slice(this.rows.indexOf(row) + 1, this.rows.indexOf(this.subtreeEnd(row)) + 1)
      .filter((below) => !isComponentRow(below)).length
    if (count === 0) return this.deleteRow(rowId, column)

    // The Kurztext alone, from its title — the cell also holds the notes under it.
    const nameCell = row.querySelector('td[data-column="short_text"]')
    const name = nameCell?.querySelector("[title]")?.title ?? nameCell?.textContent.trim() ?? ""
    // Replaced by function: a replacement string would expand a `$&` typed into the Kurztext.
    const text = this.messagesValue.confirm_delete_group
      .replace("%{name}", () => name)
      .replace("%{count}", () => count)
    this.confirmDelete({ rowId, column }, text)
  }

  confirmDelete(pending, text) {
    this.pendingDelete = pending
    this.notify(text, { sticky: true })
  }

  dismissDelete() {
    this.pendingDelete = null
    this.clearNotice()
  }

  answerDelete(event, key) {
    const { rowId, column, confirmed } = this.pendingDelete
    this.dismissDelete()
    if (key.key === "Escape") return event.preventDefault()
    if (key.key !== "Enter" || key.ctrlKey || key.metaKey || key.altKey) return

    event.preventDefault()
    this.deleteRow(rowId, column, { confirmed })
  }

  // `grid` tells #destroy that a row it could not bring back must be confirmed first; it answers
  // 409 with the reason, and the confirmed second request deletes.
  deleteRow(rowId, column, { confirmed = false } = {}) {
    const body = new FormData()
    body.append("grid", "1")
    if (confirmed) body.append("confirmed", "1")
    this.send(
      "DELETE",
      `${this.itemsUrlValue}/${rowId}`,
      body,
      { rowId: this.neighbourOf(rowId), column },
      { onConflict: (reason) => this.confirmDelete({ rowId, column, confirmed: true }, reason) },
    )
  }

  // Where the selection goes once the row is gone: the next row on screen after its subtree, or
  // the one above it. Counted over all rows — a folded Titel's subtree ends on a hidden row.
  neighbourOf(rowId) {
    const rows = this.rows
    const row = this.row(rowId)
    const after = rows
      .slice(rows.indexOf(this.subtreeEnd(row)) + 1)
      .find((candidate) => !candidate.hidden)
    const before = rows.slice(0, rows.indexOf(row)).findLast((candidate) => !candidate.hidden)
    return (after ?? before)?.dataset.rowId ?? null
  }

  // -- requests ---------------------------------------------------------------

  // The answer is a stream that syncs the changed rows or replaces the whole grid (or, for a
  // refusal such as a referenced position, a flash). A 409 is a question, not a failure: its text
  // goes to `onConflict`.
  async send(method, url, body, selection, { onConflict } = {}) {
    handoff.set(this.storageKeyValue, selection)
    try {
      const response = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
        },
        body,
      })
      const html = await response.text()
      if (!response.ok) handoff.delete(this.storageKeyValue)
      if (response.status === 409 && onConflict) return onConflict(html)
      if ((response.headers.get("Content-Type") || "").includes("text/vnd.turbo-stream.html")) {
        window.Turbo.renderStreamMessage(html)
        // A refused row (its errors arrive as a flash) stays open for the next try.
        if (!response.ok) this.reopenDraft()
      } else {
        this.failed()
      }
    } catch (error) {
      Honeybadger.notify(error, { context: { url } })
      handoff.delete(this.storageKeyValue)
      this.failed()
    }
  }

  failed() {
    this.reopenDraft()
    this.notify(this.messagesValue.request_failed)
  }

  reopenDraft() {
    if (this.draft) this.draft.sending = false
    this.draft?.input.focus()
  }

  restore({ rowId, column, knownIds, fromServer, closePicker, openText }) {
    if (closePicker) this.closeCatalogPicker()
    let row = this.row(fromServer ? this.selectValue : rowId)
    if (knownIds) row = this.rows.find((candidate) => !knownIds.has(candidate.dataset.rowId))
    if (!row) return

    // An undo can bring a row back inside a folded Titel, a new row can miss the search or a filter:
    // its chain is unfolded and position-tree keeps it on screen until the view changes.
    if (row.hidden)
      this.dispatch("expand", { detail: { ids: this.ancestorIds(row), reveal: row.dataset.rowId } })
    this.select({ rowId: row.dataset.rowId, column })
    if (openText) this.dispatch("open-text", { detail: { rowId: row.dataset.rowId, column } })
  }

  // The picker stays open across inserts when used by mouse; opened by Strg+K for one row, it has
  // done its job once that row is in.
  closeCatalogPicker() {
    const host = this.element.closest('[data-controller~="boq-catalog-picker"]')
    const picker =
      host && this.application.getControllerForElementAndIdentifier(host, "boq-catalog-picker")
    picker?.close()
  }

  ancestorIds(row) {
    const ids = []
    let parent = this.row(row.dataset.parentId)
    while (parent) {
      ids.push(parent.dataset.rowId)
      parent = this.row(parent.dataset.parentId)
    }
    return ids
  }

  select({ rowId, column }) {
    const row = this.row(rowId)
    const cell =
      row?.querySelector(`td[data-column="${CSS.escape(column)}"]`) ??
      row?.querySelector("td[data-column]")
    cell?.click()
  }

  // -- notice -----------------------------------------------------------------

  notify(text, { sticky = false } = {}) {
    if (!this.hasNoticeTarget || !text) return

    clearTimeout(this.noticeTimer)
    this.noticeTarget.textContent = text
    this.noticeTarget.hidden = false
    if (!sticky) this.noticeTimer = setTimeout(() => this.clearNotice(), 4000)
  }

  clearNotice() {
    if (!this.hasNoticeTarget) return

    clearTimeout(this.noticeTimer)
    this.noticeTarget.hidden = true
  }
}
